import React, { useState, useEffect, useCallback } from "react";
import { Pagination, Stack } from "@mui/material";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import DateRangeFilter from "../components/common/DateRangeFilter";
import AuditLogCard from "../components/auditLogs/AuditLogCard";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getAuditLogs } from "../redux/actions/auditLogActions";
import { ACTION_TYPE_OPTIONS } from "../shared/data/enumOptions";
import { ENTITY_TYPE_OPTIONS } from "../shared/data/enumOptions";
import { ActionType } from "../shared/enums/ActionType";
import { EntityType } from "../shared/enums/EntityType";

const AuditLogsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const today = new Date().toISOString().split("T")[0];

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedActionType, setSelectedActionType] = useState<string>("");
  const [selectedEntityType, setSelectedEntityType] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const limit = 12;

  const {
    items: auditLogs,
    pagination,
    loading,
    error,
  } = useAppSelector((state) => state.auditLog);

  const canRead = hasPermission("audit_log:read_all");

  const fetchLogs = useCallback((): void => {
    if (!canRead) return;
    dispatch(
      getAuditLogs({
        page,
        size: limit,
        search: debouncedSearch.trim() || undefined,
        startDate,
        endDate,
        branchId: selectedBranchId || undefined,
        actionType: (selectedActionType as ActionType) || undefined,
        entityType: (selectedEntityType as EntityType) || undefined,
      }),
    );
  }, [
    dispatch,
    page,
    limit,
    debouncedSearch,
    startDate,
    endDate,
    selectedBranchId,
    selectedActionType,
    selectedEntityType,
    canRead,
  ]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    startDate,
    endDate,
    selectedBranchId,
    selectedActionType,
    selectedEntityType,
  ]);

  return (
    <PageContainer
      title="System Audit Logs"
      subtitle="Track user actions, data revisions, and security events."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by user email, entity ID, or description..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={setSelectedBranchId}
        />

        <AppSelectFilter
          label="Action Type"
          value={selectedActionType}
          onChange={setSelectedActionType}
          options={ACTION_TYPE_OPTIONS}
          allLabel="All Actions"
          minWidth={140}
        />

        <AppSelectFilter
          label="Entity Type"
          value={selectedEntityType}
          onChange={setSelectedEntityType}
          options={ENTITY_TYPE_OPTIONS}
          allLabel="All Entities"
          minWidth={150}
        />

        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      <CardGridContainer
        loading={loading}
        error={error}
        itemCount={auditLogs?.length || 0}
        emptyMessage="No audit logs found for the selected criteria"
        onRetry={fetchLogs}
      >
        {auditLogs.map((log) => (
          <AuditLogCard key={log.id} auditLog={log} />
        ))}
      </CardGridContainer>

      {pagination?.totalPages && pagination.totalPages > 1 && (
        <Stack sx={{ alignItems: "center", mt: 4 }}>
          <Pagination
            count={pagination.totalPages}
            page={page}
            onChange={(_, val) => setPage(val)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
    </PageContainer>
  );
};

export default AuditLogsPage;
